import { Box, Button, Chip, Group, ScrollArea, Stack, Text } from "@mantine/core";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  DataTable,
  EmptyState,
  ErrorState,
  ExportMenu,
  LoadingSkeleton,
  Metric,
  RecordCard,
  SectionCard,
  StateBadge,
  useColumns,
} from "../../components/ui";
import { api, type StockMovement } from "../../lib/api";
import { Page } from "../../shell/Page";

const REASON_LABELS: Record<string, string> = {
  STOCK_IN: "Goods received",
  BILL_DEDUCTION: "Billed out",
  CORRECTION: "Count correction",
};

/**
 * Which badge token each reason wears. Goods in is a gain, billed out is
 * routine movement, a correction is the one somebody chose by hand - and that
 * is the one worth being able to pick out of a long list.
 */
/** The brief's four filter tabs, in its order. */
const FILTERS = [
  { value: "", label: "Every movement" },
  { value: "STOCK_IN", label: "Goods received" },
  { value: "BILL_DEDUCTION", label: "Billed out" },
  { value: "CORRECTION", label: "Count corrections" },
] as const;

const REASON_STATE = {
  STOCK_IN: "dlvDelivered",
  BILL_DEDUCTION: "svcReceived",
  CORRECTION: "dlvPending",
} as const;

/**
 * The movement ledger.
 *
 * Read-only, and not by convention: inventory_transactions rejects UPDATE and
 * DELETE at the database, so there is no write path to offer. Every row shows
 * the balance AFTER it, which is what makes a disputed quantity traceable
 * backwards to the entry that caused it.
 */
export function MovementLedger() {
  const [params, setParams] = useSearchParams();
  const productId = params.get("productId") ?? "";
  const reason = params.get("reason") ?? "";

  const [movements, setMovements] = useState<StockMovement[]>();
  const [error, setError] = useState<string>();

  async function load() {
    try {
      setError(undefined);
      const { movements: loaded } = productId
        ? await api.productMovements(productId)
        : await api.stockMovements(reason ? { reason } : {});
      setMovements(loaded);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load the ledger.");
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, reason]);

  const subject = productId ? movements?.[0]?.product : undefined;

  const columns = useColumns<StockMovement>(
    () => [
      {
        key: "When",
        header: "When",
        width: "170px",
        render: (movement) => (
          <Text fz={13} className="num" c="gray.7">
            {new Date(movement.createdAt).toLocaleString("en-IN", {
              day: "numeric",
              month: "short",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </Text>
        ),
      },
      {
        key: "Product & why",
        header: "Product & why",
        width: "auto",
        // The reason LINE, not just the badge: the supplier's document
        // number, the bill number or the correction's note is the answer to
        // "why did this quantity change", and it is the whole point of a
        // ledger you cannot edit.
        render: (movement) => (
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Group gap={6} wrap="nowrap">
              <Text fz={14} truncate style={{ minWidth: 0 }}>
                {movement.product.name}
              </Text>
              <StateBadge
                state={
                  REASON_STATE[movement.reason as keyof typeof REASON_STATE] ??
                  "svcReceived"
                }
                label={REASON_LABELS[movement.reason] ?? movement.reason}
              />
            </Group>
            <Text fz={12} c="gray.6" truncate>
              {movement.product.sku}
              {movement.notes ? ` · ${movement.notes}` : ""}
            </Text>
          </Stack>
        ),
      },
      {
        key: "Who",
        header: "Who",
        width: "160px",
        render: (movement) => (
          <Text fz={14} c="gray.7" truncate>
            {movement.createdBy?.name ?? "—"}
          </Text>
        ),
      },
      {
        key: "Change",
        header: "Change",
        width: "110px",
        align: "right",
        // Signed, always. "12" and "-12" are opposite events and a ledger
        // that dropped the sign would make them look the same.
        render: (movement) => (
          <Text
            fz={15}
            fw={700}
            className="num"
            c={movement.quantityDelta > 0 ? "teal.8" : "red.8"}
          >
            {movement.quantityDelta > 0 ? "+" : ""}
            {movement.quantityDelta}
          </Text>
        ),
      },
      {
        key: "Balance after",
        header: "Balance after",
        width: "140px",
        align: "right",
        render: (movement) => (
          <Text fz={14} className="num">
            {movement.runningBalanceAfter}
          </Text>
        ),
      },
    ],
    [],
  );

  return (
    <Page
      title="Movement ledger"
      subtitle={
        subject
          ? `${subject.sku} · ${movements?.length ?? 0} movements`
          : movements
            ? `${movements.length} most recent`
            : undefined
      }
      back="/inventory"
      width="wide"
    >
      {error && <ErrorState message={error} onRetry={() => void load()} />}

      <SectionCard title="What you are looking at">
        <Stack gap="sm">
          {productId ? (
            <>
              <Text fz="md" c="gray.7">
                Showing one product&apos;s full history, newest first.
              </Text>
              <Button variant="default" onClick={() => setParams({})}>
                Show every product
              </Button>
            </>
          ) : (
            // Tabs rather than a dropdown, per the brief: four options that
            // each answer "show me only this" are four things to look at, and
            // a closed select hides three of them behind a click.
            <ScrollArea type="never">
              <Group gap="xs" wrap="nowrap">
                {FILTERS.map((filter) => (
                  <Chip
                    key={filter.value || "all"}
                    checked={reason === filter.value}
                    variant={reason === filter.value ? "filled" : "outline"}
                    onClick={() =>
                      setParams(filter.value ? { reason: filter.value } : {})
                    }
                  >
                    {filter.label}
                  </Chip>
                ))}
              </Group>
            </ScrollArea>
          )}
          <Text fz="sm" c="gray.6">
            Nothing here can be edited or removed — the table rejects both at
            the database. A mistake is fixed by a further entry, so both remain
            visible.
          </Text>
        </Stack>
      </SectionCard>

      {!movements && !error && <LoadingSkeleton variant="list" count={6} />}

      {movements?.length === 0 && (
        <EmptyState title="No movements recorded under that filter" />
      )}

      {/* Desktop: the ledger as a table. No row opens anything - there is
          nothing to open, and nothing to edit: inventory_transactions rejects
          UPDATE and DELETE at the database, so this table offers no onOpen
          rather than offering one that would do nothing. */}
      {movements && movements.length > 0 && (
        <Box visibleFrom="md">
          <Stack gap={12}>
            <Group justify="flex-end">
              <ExportMenu dataset="movements" />
            </Group>
            <DataTable
              rows={movements}
              columns={columns}
              rowKey={(movement) => movement.id}
              caption={`Movement ledger: ${movements.length} rows`}
            />
          </Stack>
        </Box>
      )}

      {/* Phone: the same rows as cards. */}
      <Box hiddenFrom="md">
      <Stack gap="sm">
      {movements?.map((movement) => (
        <RecordCard
          key={movement.id}
          primary={movement.product.name}
          secondary={movement.product.sku}
          badges={
            <StateBadge
              state={
                REASON_STATE[movement.reason as keyof typeof REASON_STATE] ??
                "svcReceived"
              }
              label={REASON_LABELS[movement.reason] ?? movement.reason}
            />
          }
          meta={
            <>
              {new Date(movement.createdAt).toLocaleString()}
              {movement.createdBy ? ` · ${movement.createdBy.name}` : ""}
              {movement.notes ? ` · ${movement.notes}` : ""}
            </>
          }
          action={
            <Stack gap={0} align="flex-end" style={{ flexShrink: 0 }}>
              <Metric
                value={`${movement.quantityDelta > 0 ? "+" : ""}${movement.quantityDelta}`}
                size="kpi"
              />
              <Text fz="xs" c="gray.6" className="num">
                → {movement.runningBalanceAfter} on hand
              </Text>
            </Stack>
          }
        />
      ))}
      </Stack>
      </Box>
    </Page>
  );
}
